/*
 * Carbisdale - Konfiguration
 *
 * Zentrale Stelle für alle Konstanten und Einstellungen.
 * Änderungen am Look & Feel sollten hier gemacht werden,
 * nicht in den einzelnen Modulen verstreut.
 */

// === Intensitätsstufen für Zeichen ===
// Werden nur in monochromen Modi (NERD, DARK, LIGHT) verwendet
export const Intensity = Object.freeze({
    LOW: 'LOW',
    MEDIUM: 'MEDIUM',
    HIGH: 'HIGH'
})


// === Farb-Palette für Colored Modes (VS Code inspired) ===
export const COLORS = Object.freeze({
    // === Dark Mode Palette ===
    // Text & Content
    D_TEXT: '#d4d4d4',           // Normaler Text (hellgrau)
    D_TEXT_BOLD: '#ffffff',      // Fetter Text (weiß)
    D_TEXT_ITALIC: '#9cdcfe',    // Kursiver Text (hellblau)
    D_TEXT_DIM: '#808080',       // Gedämpfter Text (grau)

    // Syntax-Farben (VS Code One Dark inspired)
    D_KEYWORD: '#c678dd',        // Keywords, Marker (lila)
    D_STRING: '#98c379',         // Strings, Links (grün)
    D_FUNCTION: '#61afef',       // Functions, Headings (blau)
    D_VARIABLE: '#e06c75',       // Variables (rot/rosa)
    D_TYPE: '#e5c07b',           // Types, Numbers (gelb)
    D_COMMENT: '#5c6370',        // Comments (dunkelgrau)
    D_OPERATOR: '#56b6c2',       // Operators (cyan)

    // UI-Farben Dark
    D_BORDER: '#3c3c3c',         // Rahmen (dunkelgrau)
    D_BORDER_ACTIVE: '#007acc',  // Aktiver Rahmen (VS Code blau)
    D_ACCENT: '#007acc',         // Akzent (VS Code blau)
    D_SELECTION: '#264f78',      // Selection-Hintergrund

    // === Light Mode Palette ===
    // Text & Content
    L_TEXT: '#333333',           // Normaler Text (dunkelgrau)
    L_TEXT_BOLD: '#000000',      // Fetter Text (schwarz)
    L_TEXT_ITALIC: '#0451a5',    // Kursiver Text (blau)
    L_TEXT_DIM: '#808080',       // Gedämpfter Text

    // Syntax-Farben Light
    L_KEYWORD: '#af00db',        // Keywords, Marker (magenta)
    L_STRING: '#098658',         // Strings, Links (grün)
    L_FUNCTION: '#0000ff',       // Functions, Headings (blau)
    L_VARIABLE: '#a31515',       // Variables (rot)
    L_TYPE: '#267f99',           // Types (türkis)
    L_COMMENT: '#6a9955',        // Comments (grün-grau)
    L_OPERATOR: '#0000ff',       // Operators (blau)

    // UI-Farben Light
    L_BORDER: '#c0c0c0',         // Rahmen (hellgrau)
    L_BORDER_ACTIVE: '#005a9e',  // Aktiver Rahmen (dunkelblau)
    L_ACCENT: '#005a9e',         // Akzent
    L_SELECTION: '#add6ff',      // Selection-Hintergrund

    // Background-Bereich (Story Bible) — warm/Sepia
    D_BACKGROUND: '#b8a080',     // Sepia (Dark Mode)
    L_BACKGROUND: '#6b5b4b',     // Gedämpftes Braun (Light Mode)
    D_BACKGROUND_BOLD: '#d4c0a0', // Sepia hell (Dark Bold)
    L_BACKGROUND_BOLD: '#4a3c2e', // Dunkleres Braun (Light Bold)
    D_BACKGROUND_DIM: '#8a7060',  // Sepia gedämpft (Dark Dim)
    L_BACKGROUND_DIM: '#8a7a6a',  // Helles Braun (Light Dim)

    // Legacy (für Abwärtskompatibilität)
    ORANGE: '#ff9500',
    GREEN: '#00ff66',
    RED: '#ff4444',

    // Spellcheck Wavy-Underline (additive Dekoration, kein Style)
    SPELLCHECK_WAVY_DARK:  '#cc4444',   // Color-Dark-Modes: dezent rot
    SPELLCHECK_WAVY_LIGHT: '#aa2222',   // Color-Light-Modes: dezent rot
    SPELLCHECK_WAVY_NERD:  '#66ff66',   // Ultra/Regular Nerd: Phosphor-Grün, Intensity.HIGH
    SPELLCHECK_WAVY_INACTIVE_ALPHA: 0.7 // globalAlpha-Faktor fuer Wellen ausserhalb des aktiven Absatzes
})

// === Character Styles ===
// Jeder Style definiert das Aussehen für alle Display Modes
export const Style = Object.freeze({
    // Standard-Styles
    DEFAULT: 'DEFAULT',

    // Menü-Styles
    MENU_BORDER: 'MENU_BORDER',
    MENU_TITLE: 'MENU_TITLE',
    MENU_TITLE_BG: 'MENU_TITLE_BG',                   // Hintergrund-Streifen hinter Title (Title-Strip)
    MENU_ITEM: 'MENU_ITEM',
    MENU_ITEM_KEY: 'MENU_ITEM_KEY',
    MENU_ITEM_SELECTED: 'MENU_ITEM_SELECTED',
    MENU_ITEM_KEY_SELECTED: 'MENU_ITEM_KEY_SELECTED',
    MENU_ITEM_SELECTED_BG: 'MENU_ITEM_SELECTED_BG',   // Vollflaechiger Stripe analog Active-Row im Editor
    MENU_BACKGROUND: 'MENU_BACKGROUND',

    // Context-Menu Styles
    MENU_ITEM_HOVER: 'MENU_ITEM_HOVER',         // Hover ohne Bold (kein Text-Springen)
    MENU_ITEM_BOLD: 'MENU_ITEM_BOLD',
    MENU_ITEM_ITALIC: 'MENU_ITEM_ITALIC',
    MENU_ITEM_UNDERLINE: 'MENU_ITEM_UNDERLINE',
    MENU_ITEM_STRIKE: 'MENU_ITEM_STRIKE',
    MENU_ITEM_CODE: 'MENU_ITEM_CODE',           // Monospaced — Preview fuer Raw (Block) + Code (Inline)
    // Hover-Varianten der Preview-Styles: Hover-Farbe (D_TYPE/L_TYPE) +
    // jeweiliges Format-Flag erhalten. Damit bleibt das Format-Preview
    // beim Hover erkennbar; nur die Bold-Akzent-Geste entfaellt fuer
    // MENU_ITEM_ITALIC/UNDERLINE/STRIKE/CODE, damit Text nicht springt.
    MENU_ITEM_BOLD_HOVER: 'MENU_ITEM_BOLD_HOVER',
    MENU_ITEM_ITALIC_HOVER: 'MENU_ITEM_ITALIC_HOVER',
    MENU_ITEM_UNDERLINE_HOVER: 'MENU_ITEM_UNDERLINE_HOVER',
    MENU_ITEM_STRIKE_HOVER: 'MENU_ITEM_STRIKE_HOVER',
    MENU_ITEM_CODE_HOVER: 'MENU_ITEM_CODE_HOVER',
    // Grayed-Varianten der Preview-Styles: gedaempfte Farbe (PANE_INFO,
    // LOW + D_COMMENT/L_COMMENT) + jeweiliges Format-Flag erhalten.
    // Damit zeigt auch ein ausgegrautes Bold/Italic/Under/Strike/Code-Item
    // sein Format als Preview.
    MENU_ITEM_BOLD_GRAY: 'MENU_ITEM_BOLD_GRAY',
    MENU_ITEM_ITALIC_GRAY: 'MENU_ITEM_ITALIC_GRAY',
    MENU_ITEM_UNDERLINE_GRAY: 'MENU_ITEM_UNDERLINE_GRAY',
    MENU_ITEM_STRIKE_GRAY: 'MENU_ITEM_STRIKE_GRAY',
    MENU_ITEM_CODE_GRAY: 'MENU_ITEM_CODE_GRAY',

    // Dialog-Styles (Copyright-Box etc.)
    DIALOG_BORDER: 'DIALOG_BORDER',
    DIALOG_TEXT: 'DIALOG_TEXT',

    // Dummy-Content für Tests
    DUMMY_LOW: 'DUMMY_LOW',
    DUMMY_MEDIUM: 'DUMMY_MEDIUM',
    DUMMY_HIGH: 'DUMMY_HIGH',

    // Pane-Styles (Screen Layout)
    PANE_BORDER: 'PANE_BORDER',                 // Rahmen inaktiver Bereiche
    PANE_BORDER_ACTIVE: 'PANE_BORDER_ACTIVE',   // Rahmen aktiver Bereich
    PANE_TITLE: 'PANE_TITLE',                   // Titel inaktiver Bereiche
    PANE_TITLE_ACTIVE: 'PANE_TITLE_ACTIVE',     // Titel aktiver Bereich (highlighted)
    PANE_INFO: 'PANE_INFO',                     // Info-Text (Name, Größe)
    PARENT_ENTRY: 'PARENT_ENTRY',               // ◁ back to parent folder im Project-Dashboard (PANE_INFO + isItalic)
    CONTROL_NORMAL: 'CONTROL_NORMAL',           // +/- Controls normal
    CONTROL_HIGHLIGHT: 'CONTROL_HIGHLIGHT',     // +/- Controls im Nav-Modus
    CONTROL_HOVER: 'CONTROL_HOVER',             // +/- Controls bei Mouse-Hover
    CONTROL_STARTING: 'CONTROL_STARTING',       // Diktat-Control beim Starten (Orange)
    CONTROL_DICTATION_ACTIVE: 'CONTROL_DICTATION_ACTIVE', // Diktat-Control aktiv (Grün)
    CONTROL_ABORT: 'CONTROL_ABORT',             // Agent-Abort-Control (Rot, während AI arbeitet)
    SYNC_PENDING: 'SYNC_PENDING',               // Cloud-Sync ausstehend (Orange, wie CONTROL_STARTING)

    // Editor-Styles (Prose Editor)
    EDITOR_TEXT: 'EDITOR_TEXT',                 // Normaler Text
    EDITOR_BOLD: 'EDITOR_BOLD',                 // Fetter Text
    EDITOR_ITALIC: 'EDITOR_ITALIC',             // Kursiver Text
    EDITOR_BOLD_ITALIC: 'EDITOR_BOLD_ITALIC',   // Fett + Kursiv
    EDITOR_STRIKETHROUGH: 'EDITOR_STRIKETHROUGH', // Durchgestrichen
    EDITOR_UNDERLINE: 'EDITOR_UNDERLINE',       // Unterstrichen
    EDITOR_HEADING_1: 'EDITOR_HEADING_1',       // Überschrift H1
    EDITOR_HEADING_2: 'EDITOR_HEADING_2',       // Überschrift H2
    EDITOR_HEADING_3: 'EDITOR_HEADING_3',       // Überschrift H3
    EDITOR_HEADING_4: 'EDITOR_HEADING_4',       // Überschrift H4
    EDITOR_HEADING_5: 'EDITOR_HEADING_5',       // Überschrift H5
    EDITOR_HEADING_6: 'EDITOR_HEADING_6',       // Überschrift H6
    EDITOR_LINK: 'EDITOR_LINK',                 // Section-Links (unterstrichen, farbig)
    EDITOR_LINK_BROKEN: 'EDITOR_LINK_BROKEN',   // Ungültige Section-Links (durchgestrichen)
    EDITOR_MARKER: 'EDITOR_MARKER',             // Absatz-Marker (>, <)
    EDITOR_MARKER_DIM: 'EDITOR_MARKER_DIM',     // Gedimmte Marker (Cursor nicht in diesem Pane/Absatz)
    EDITOR_BLOCKQUOTE: 'EDITOR_BLOCKQUOTE',     // > am Anfang
    EDITOR_BLOCKQUOTE_UNITALIC: 'EDITOR_BLOCKQUOTE_UNITALIC', // *italic* innerhalb Blockquote → Kontrast durch Nicht-Kursiv
    EDITOR_HR: 'EDITOR_HR',                     // Horizontale Linie (---)
    SEPARATOR_TITLE: 'SEPARATOR_TITLE',         // Separator-Titel (bold, HR-Farbe)
    EDITOR_CODE: 'EDITOR_CODE',                 // Inline-Code (`...`)
    EDITOR_PLACEHOLDER: 'EDITOR_PLACEHOLDER',   // Empty-State-Hint im Memory-Editor

    // Delta-Review-Styles (Inline-Diff)
    EDITOR_REVIEW_KEEP: 'EDITOR_REVIEW_KEEP',   // Common-Anteil (unveraenderter Text)
    EDITOR_REVIEW_SHOWN: 'EDITOR_REVIEW_SHOWN', // Aktive Version (proposed bei activeView=proposed)
    EDITOR_REVIEW_STRIKE: 'EDITOR_REVIEW_STRIKE', // Inaktive Version (durchgestrichen, gedimmt)

    // Selection-Styles
    EDITOR_SELECTION: 'EDITOR_SELECTION',       // Selektierter Text
    EDITOR_CURSOR: 'EDITOR_CURSOR',             // Cursor-Block

    // Search-Styles
    SEARCH_MATCH: 'SEARCH_MATCH',               // Normaler Suchtreffer
    SEARCH_CURRENT_MATCH: 'SEARCH_CURRENT_MATCH', // Aktueller Suchtreffer

    // Scrollbar-Styles
    SCROLLBAR_TRACK: 'SCROLLBAR_TRACK',         // Scrollbar-Hintergrund
    SCROLLBAR_THUMB: 'SCROLLBAR_THUMB',         // Scrollbar-Handle

    // Outline-Styles
    OUTLINE_CONNECTOR: 'OUTLINE_CONNECTOR',           // │ └ ├ Zeichen (gedämpft)
    OUTLINE_CURRENT: 'OUTLINE_CURRENT',               // Aktueller Absatz (hervorgehoben)
    OUTLINE_NOTE: 'OUTLINE_NOTE',                      // Note-Text im Outline-Pane

    // Console-Styles (Core Pane)
    CONSOLE_SYSTEM: 'CONSOLE_SYSTEM',               // [SYS] Prefix + Text
    CONSOLE_USER: 'CONSOLE_USER',                   // > Prefix + User-Text
    CONSOLE_AI: 'CONSOLE_AI',                       // AI-Antwort-Text
    CONSOLE_ERROR: 'CONSOLE_ERROR',                 // [ERR] Prefix + Text
    CONSOLE_THINKING: 'CONSOLE_THINKING',           // Thinking-Block (kursiv, gedaempft)
    CONSOLE_REVIEW_PANEL: 'CONSOLE_REVIEW_PANEL',   // [REV] Delta-Review-Steuerung
    CONSOLE_THINKING_COLLAPSED: 'CONSOLE_THINKING_COLLAPSED',  // Placeholder '[...]' — wie THINKING, aber nicht kursiv
    CONSOLE_PROMPT: 'CONSOLE_PROMPT',               // > Zeichen der Eingabe
    CONSOLE_INPUT: 'CONSOLE_INPUT',                 // Eingabe-Text
    CONSOLE_LOCK: 'CONSOLE_LOCK',                   // ... im Lock-Zustand

    // Console AI Block-Styles (Markdown-Bloecke in AI-Antworten)
    CONSOLE_AI_HEADING: 'CONSOLE_AI_HEADING',       // Ueberschriften (Bold)
    CONSOLE_AI_CODE: 'CONSOLE_AI_CODE',             // Code-Bloecke (Monospace, gedaempft)
    CONSOLE_AI_BLOCKQUOTE: 'CONSOLE_AI_BLOCKQUOTE', // Blockquotes (Kursiv)
    CONSOLE_AI_BLOCKQUOTE_UNITALIC: 'CONSOLE_AI_BLOCKQUOTE_UNITALIC', // *italic* innerhalb Blockquote → Kontrast

    // Link-Style (klickbare URLs in allen Entry-Typen)
    CONSOLE_LINK: 'CONSOLE_LINK'                    // URL-Links (Underline, heller)
})

// === Style-Definitionen ===
// Für jeden Style: intensity (für monochrome Modi), isBold, darkColor, lightColor
export const STYLES = Object.freeze({
    [Style.DEFAULT]: Object.freeze({
        intensity: Intensity.MEDIUM,
        isBold: false,
        darkColor: COLORS.D_TEXT,
        lightColor: COLORS.L_TEXT
    }),

    // Menü-Styles
    [Style.MENU_BORDER]: Object.freeze({
        intensity: Intensity.HIGH,
        isBold: false,
        darkColor: COLORS.D_BORDER_ACTIVE,
        lightColor: COLORS.L_BORDER_ACTIVE
    }),
    [Style.MENU_TITLE]: Object.freeze({
        intensity: Intensity.HIGH,
        isBold: true,
        darkColor: COLORS.D_FUNCTION,
        lightColor: COLORS.L_FUNCTION
    }),
    // Title-Strip: blickdichter Hintergrund-Streifen hinter dem Titel-Text.
    // Phase 1: Background-Verlaengerung (gleicher Ton wie Editor-BG) — der
    // Renderer mappt diesen Style auf `modeConfig.background`. Wirkt im
    // Proportional-Mode als Schutz gegen durchscheinende Glyphen-Oberlaengen
    // des Editor-Textes darunter. Die hier definierten darkColor/lightColor
    // sind Default-Fallbacks und werden nur greifen, falls jemand den Style
    // in einem anderen Kontext aufloest.
    [Style.MENU_TITLE_BG]: Object.freeze({
        intensity: Intensity.LOW,
        isBold: false,
        darkColor: COLORS.D_BORDER_ACTIVE,
        lightColor: COLORS.L_BORDER_ACTIVE
    }),
    [Style.MENU_ITEM]: Object.freeze({
        intensity: Intensity.MEDIUM,
        isBold: false,
        darkColor: COLORS.D_TEXT,
        lightColor: COLORS.L_TEXT
    }),
    [Style.MENU_ITEM_KEY]: Object.freeze({
        intensity: Intensity.MEDIUM,
        isBold: false,
        darkColor: COLORS.D_KEYWORD,
        lightColor: COLORS.L_KEYWORD
    }),
    [Style.MENU_ITEM_SELECTED]: Object.freeze({
        intensity: Intensity.HIGH,
        isBold: true,
        darkColor: COLORS.D_TYPE,
        lightColor: COLORS.L_TYPE
    }),
    [Style.MENU_ITEM_KEY_SELECTED]: Object.freeze({
        intensity: Intensity.HIGH,
        isBold: true,
        darkColor: COLORS.D_TYPE,
        lightColor: COLORS.L_TYPE
    }),
    // Vollflaechiger Stripe-Hintergrund fuer das aktuell selektierte Item.
    // Analog zum Active-Paragraph-Highlight im Editor — der Renderer mappt
    // diesen Style auf `modeConfig.activeRowBackground`. darkColor/lightColor
    // dienen nur als Default-Fallback.
    [Style.MENU_ITEM_SELECTED_BG]: Object.freeze({
        intensity: Intensity.LOW,
        isBold: false,
        darkColor: COLORS.D_BORDER,
        lightColor: COLORS.L_BORDER
    }),
    [Style.MENU_BACKGROUND]: Object.freeze({
        intensity: Intensity.HIGH,
        isBold: false,
        darkColor: COLORS.D_TEXT,
        lightColor: COLORS.L_TEXT
    }),

    // Context-Menu Styles
    [Style.MENU_ITEM_HOVER]: Object.freeze({
        intensity: Intensity.HIGH,
        isBold: false,
        darkColor: COLORS.D_TYPE,
        lightColor: COLORS.L_TYPE
    }),
    [Style.MENU_ITEM_BOLD]: Object.freeze({
        intensity: Intensity.MEDIUM,
        isBold: true,
        darkColor: COLORS.D_TEXT,
        lightColor: COLORS.L_TEXT
    }),
    [Style.MENU_ITEM_ITALIC]: Object.freeze({
        intensity: Intensity.MEDIUM,
        isBold: false,
        isItalic: true,
        darkColor: COLORS.D_TEXT,
        lightColor: COLORS.L_TEXT
    }),
    [Style.MENU_ITEM_UNDERLINE]: Object.freeze({
        intensity: Intensity.MEDIUM,
        isBold: false,
        isUnderline: true,
        darkColor: COLORS.D_TEXT,
        lightColor: COLORS.L_TEXT
    }),
    [Style.MENU_ITEM_STRIKE]: Object.freeze({
        intensity: Intensity.MEDIUM,
        isBold: false,
        isStrikethrough: true,
        darkColor: COLORS.D_TEXT,
        lightColor: COLORS.L_TEXT
    }),
    // Monospaced — Preview-Style fuer Raw (Block-Code-Toggle) und Code
    // (Inline-Code-Toggle). Farbe wie die anderen Preview-Styles (D_TEXT/
    // L_TEXT, MEDIUM); die Hover-Variante MENU_ITEM_CODE_HOVER nutzt die
    // Highlight-Farbe (D_TYPE/L_TYPE).
    [Style.MENU_ITEM_CODE]: Object.freeze({
        intensity: Intensity.MEDIUM,
        isBold: false,
        isMono: true,
        darkColor: COLORS.D_TEXT,
        lightColor: COLORS.L_TEXT
    }),

    // Hover-Varianten der Inline-Format-Previews:
    // Hover-Farbe (HIGH, D_TYPE/L_TYPE — analog MENU_ITEM_HOVER) +
    // jeweiliges Format-Flag, damit das Preview beim Hover sichtbar bleibt.
    // Italic/Underline/Strike: isBold bleibt false (kein Springen). Bei
    // BOLD_HOVER ist isBold notwendigerweise true — das Item zeigt Bold an.
    [Style.MENU_ITEM_BOLD_HOVER]: Object.freeze({
        intensity: Intensity.HIGH,
        isBold: true,
        darkColor: COLORS.D_TYPE,
        lightColor: COLORS.L_TYPE
    }),
    [Style.MENU_ITEM_ITALIC_HOVER]: Object.freeze({
        intensity: Intensity.HIGH,
        isBold: false,
        isItalic: true,
        darkColor: COLORS.D_TYPE,
        lightColor: COLORS.L_TYPE
    }),
    [Style.MENU_ITEM_UNDERLINE_HOVER]: Object.freeze({
        intensity: Intensity.HIGH,
        isBold: false,
        isUnderline: true,
        darkColor: COLORS.D_TYPE,
        lightColor: COLORS.L_TYPE
    }),
    [Style.MENU_ITEM_STRIKE_HOVER]: Object.freeze({
        intensity: Intensity.HIGH,
        isBold: false,
        isStrikethrough: true,
        darkColor: COLORS.D_TYPE,
        lightColor: COLORS.L_TYPE
    }),
    [Style.MENU_ITEM_CODE_HOVER]: Object.freeze({
        intensity: Intensity.HIGH,
        isBold: false,
        isMono: true,
        darkColor: COLORS.D_TYPE,
        lightColor: COLORS.L_TYPE
    }),

    // Grayed-Varianten der Inline-Format-Previews:
    // Farbe wie PANE_INFO (LOW, D_COMMENT/L_COMMENT) + Format-Flag erhalten.
    // So zeigt ein ausgegrautes Item weiterhin sein Format-Preview an.
    [Style.MENU_ITEM_BOLD_GRAY]: Object.freeze({
        intensity: Intensity.LOW,
        isBold: true,
        darkColor: COLORS.D_COMMENT,
        lightColor: COLORS.L_COMMENT
    }),
    [Style.MENU_ITEM_ITALIC_GRAY]: Object.freeze({
        intensity: Intensity.LOW,
        isBold: false,
        isItalic: true,
        darkColor: COLORS.D_COMMENT,
        lightColor: COLORS.L_COMMENT
    }),
    [Style.MENU_ITEM_UNDERLINE_GRAY]: Object.freeze({
        intensity: Intensity.LOW,
        isBold: false,
        isUnderline: true,
        darkColor: COLORS.D_COMMENT,
        lightColor: COLORS.L_COMMENT
    }),
    [Style.MENU_ITEM_STRIKE_GRAY]: Object.freeze({
        intensity: Intensity.LOW,
        isBold: false,
        isStrikethrough: true,
        darkColor: COLORS.D_COMMENT,
        lightColor: COLORS.L_COMMENT
    }),
    [Style.MENU_ITEM_CODE_GRAY]: Object.freeze({
        intensity: Intensity.LOW,
        isBold: false,
        isMono: true,
        darkColor: COLORS.D_COMMENT,
        lightColor: COLORS.L_COMMENT
    }),

    // Dialog-Styles
    [Style.DIALOG_BORDER]: Object.freeze({
        intensity: Intensity.HIGH,
        isBold: false,
        darkColor: COLORS.D_BORDER_ACTIVE,
        lightColor: COLORS.L_BORDER_ACTIVE
    }),
    [Style.DIALOG_TEXT]: Object.freeze({
        intensity: Intensity.HIGH,
        isBold: false,
        darkColor: COLORS.D_TEXT,
        lightColor: COLORS.L_TEXT
    }),

    // Dummy-Content (für Tests mit verschiedenen Intensitäten)
    [Style.DUMMY_LOW]: Object.freeze({
        intensity: Intensity.LOW,
        isBold: false,
        darkColor: COLORS.D_COMMENT,
        lightColor: COLORS.L_COMMENT
    }),
    [Style.DUMMY_MEDIUM]: Object.freeze({
        intensity: Intensity.MEDIUM,
        isBold: false,
        darkColor: COLORS.D_TEXT,
        lightColor: COLORS.L_TEXT
    }),
    [Style.DUMMY_HIGH]: Object.freeze({
        intensity: Intensity.HIGH,
        isBold: true,
        darkColor: COLORS.D_TEXT_BOLD,
        lightColor: COLORS.L_TEXT_BOLD
    }),

    // Pane-Styles (Screen Layout)
    [Style.PANE_BORDER]: Object.freeze({
        intensity: Intensity.LOW,
        isBold: false,
        darkColor: COLORS.D_BORDER,
        lightColor: COLORS.L_BORDER
    }),
    [Style.PANE_BORDER_ACTIVE]: Object.freeze({
        intensity: Intensity.HIGH,
        isBold: false,
        darkColor: COLORS.D_BORDER_ACTIVE,
        lightColor: COLORS.L_BORDER_ACTIVE
    }),
    [Style.PANE_TITLE]: Object.freeze({
        intensity: Intensity.MEDIUM,
        isBold: false,
        darkColor: COLORS.D_COMMENT,
        lightColor: COLORS.L_COMMENT
    }),
    [Style.PANE_TITLE_ACTIVE]: Object.freeze({
        intensity: Intensity.HIGH,
        isBold: true,
        darkColor: COLORS.D_FUNCTION,
        lightColor: COLORS.L_FUNCTION
    }),
    [Style.PANE_INFO]: Object.freeze({
        intensity: Intensity.LOW,
        isBold: false,
        darkColor: COLORS.D_COMMENT,
        lightColor: COLORS.L_COMMENT
    }),
    // Parent-Eintrag im Project-Dashboard (◁ back to parent folder).
    // Spec §14.2: 50 % grau, kursiv. Wir reuse die PANE_INFO-Farbe (die
    // ohnehin die "dim"-Variante ist) und ergaenzen isItalic, damit der
    // Eintrag visuell vom Rest abhebt ohne neue Farbe einzufuehren.
    [Style.PARENT_ENTRY]: Object.freeze({
        intensity: Intensity.LOW,
        isBold: false,
        isItalic: true,
        darkColor: COLORS.D_COMMENT,
        lightColor: COLORS.L_COMMENT
    }),
    [Style.CONTROL_NORMAL]: Object.freeze({
        intensity: Intensity.MEDIUM,
        isBold: false,
        darkColor: COLORS.D_OPERATOR,
        lightColor: COLORS.L_OPERATOR,
        // Worker Dark: Normal/Hover getauscht — hier das hellere Blau, weil das
        // dunklere Cyan-Hover sonst unauffälliger wäre als der Normalzustand.
        workerDarkColor: COLORS.D_ACCENT
    }),
    [Style.CONTROL_HIGHLIGHT]: Object.freeze({
        intensity: Intensity.HIGH,
        isBold: true,
        darkColor: COLORS.D_TYPE,
        lightColor: COLORS.L_TYPE
    }),
    [Style.CONTROL_HOVER]: Object.freeze({
        intensity: Intensity.HIGH,
        isBold: false,
        darkColor: COLORS.D_ACCENT,
        lightColor: COLORS.L_ACCENT,
        // Worker Dark: Hover trägt das hellere Cyan (Tausch mit Normal) — wirkt
        // beim Überfahren heller statt dunkler.
        workerDarkColor: COLORS.D_OPERATOR
    }),
    [Style.CONTROL_STARTING]: Object.freeze({
        intensity: Intensity.HIGH,
        isBold: true,
        darkColor: COLORS.ORANGE,
        lightColor: COLORS.ORANGE
    }),
    [Style.CONTROL_DICTATION_ACTIVE]: Object.freeze({
        intensity: Intensity.HIGH,
        isBold: true,
        darkColor: COLORS.GREEN,
        lightColor: COLORS.GREEN
    }),
    [Style.CONTROL_ABORT]: Object.freeze({
        intensity: Intensity.HIGH,
        isBold: true,
        darkColor: COLORS.RED,
        lightColor: COLORS.RED
    }),
    [Style.SYNC_PENDING]: Object.freeze({
        intensity: Intensity.HIGH,
        isBold: true,
        darkColor: COLORS.ORANGE,
        lightColor: COLORS.ORANGE
    }),

    // === Editor-Styles (VS Code inspired) ===
    [Style.EDITOR_TEXT]: Object.freeze({
        intensity: Intensity.MEDIUM,
        isBold: false,
        isItalic: false,
        isStrikethrough: false,
        isUnderline: false,
        darkColor: COLORS.D_TEXT,
        lightColor: COLORS.L_TEXT,
        darkColorBg: COLORS.D_BACKGROUND,
        lightColorBg: COLORS.L_BACKGROUND,
        intensityBg: Intensity.LOW
    }),
    [Style.EDITOR_BOLD]: Object.freeze({
        intensity: Intensity.HIGH,
        isBold: true,
        isItalic: false,
        isStrikethrough: false,
        isUnderline: false,
        darkColor: COLORS.D_TEXT_BOLD,
        lightColor: COLORS.L_TEXT_BOLD,
        darkColorBg: COLORS.D_BACKGROUND_BOLD,
        lightColorBg: COLORS.L_BACKGROUND_BOLD,
        intensityBg: Intensity.MEDIUM
    }),
    [Style.EDITOR_ITALIC]: Object.freeze({
        intensity: Intensity.MEDIUM,
        isBold: false,
        isItalic: true,
        isStrikethrough: false,
        isUnderline: false,
        darkColor: COLORS.D_TEXT_ITALIC,
        lightColor: COLORS.L_TEXT_ITALIC,
        darkColorBg: COLORS.D_BACKGROUND,
        lightColorBg: COLORS.L_BACKGROUND,
        intensityBg: Intensity.LOW
    }),
    [Style.EDITOR_BOLD_ITALIC]: Object.freeze({
        intensity: Intensity.HIGH,
        isBold: true,
        isItalic: true,
        isStrikethrough: false,
        isUnderline: false,
        darkColor: COLORS.D_TEXT_BOLD,
        lightColor: COLORS.L_TEXT_BOLD,
        darkColorBg: COLORS.D_BACKGROUND_BOLD,
        lightColorBg: COLORS.L_BACKGROUND_BOLD,
        intensityBg: Intensity.MEDIUM
    }),
    [Style.EDITOR_STRIKETHROUGH]: Object.freeze({
        intensity: Intensity.MEDIUM,
        isBold: false,
        isItalic: false,
        isStrikethrough: true,
        isUnderline: false,
        darkColor: COLORS.D_TEXT_DIM,
        lightColor: COLORS.L_TEXT_DIM,
        darkColorBg: COLORS.D_BACKGROUND_DIM,
        lightColorBg: COLORS.L_BACKGROUND_DIM,
        intensityBg: Intensity.LOW
    }),
    [Style.EDITOR_REVIEW_KEEP]: Object.freeze({
        intensity: Intensity.MEDIUM,
        isBold: false,
        isItalic: false,
        isStrikethrough: false,
        isUnderline: false,
        darkColor: COLORS.D_TEXT,
        lightColor: COLORS.L_TEXT,
        darkColorBg: COLORS.D_BACKGROUND,
        lightColorBg: COLORS.L_BACKGROUND,
        intensityBg: Intensity.LOW
    }),
    [Style.EDITOR_REVIEW_SHOWN]: Object.freeze({
        intensity: Intensity.HIGH,
        isBold: false,
        isItalic: false,
        isStrikethrough: false,
        isUnderline: false,
        darkColor: COLORS.D_STRING,
        lightColor: COLORS.L_STRING,
        darkColorBg: COLORS.D_BACKGROUND,
        lightColorBg: COLORS.L_BACKGROUND,
        intensityBg: Intensity.LOW
    }),
    [Style.EDITOR_REVIEW_STRIKE]: Object.freeze({
        intensity: Intensity.LOW,
        isBold: false,
        isItalic: false,
        isStrikethrough: true,
        isUnderline: false,
        darkColor: COLORS.D_TEXT_DIM,
        lightColor: COLORS.L_TEXT_DIM,
        darkColorBg: COLORS.D_BACKGROUND_DIM,
        lightColorBg: COLORS.L_BACKGROUND_DIM,
        intensityBg: Intensity.LOW
    }),
    [Style.EDITOR_UNDERLINE]: Object.freeze({
        intensity: Intensity.MEDIUM,
        isBold: false,
        isItalic: false,
        isStrikethrough: false,
        isUnderline: true,
        darkColor: COLORS.D_TEXT,
        lightColor: COLORS.L_TEXT,
        darkColorBg: COLORS.D_BACKGROUND,
        lightColorBg: COLORS.L_BACKGROUND,
        intensityBg: Intensity.LOW
    }),
    // Heading-Paare: H1=H2, H3=H4, H5=H6 sind bewusst identisch gestaltet.
    // Visuelle Unterscheidung erfolgt über drei Farbstufen (Blau/Lila/Gelb), nicht über sechs Level.
    [Style.EDITOR_HEADING_1]: Object.freeze({
        intensity: Intensity.HIGH,
        isBold: true,
        isItalic: false,
        isStrikethrough: false,
        isUnderline: false,
        darkColor: COLORS.D_FUNCTION,       // Blau wie Functions
        lightColor: COLORS.L_FUNCTION,
        darkColorBg: COLORS.D_BACKGROUND_BOLD,
        lightColorBg: COLORS.L_BACKGROUND_BOLD,
        intensityBg: Intensity.MEDIUM
    }),
    [Style.EDITOR_HEADING_2]: Object.freeze({
        intensity: Intensity.HIGH,
        isBold: true,
        isItalic: false,
        isStrikethrough: false,
        isUnderline: false,
        darkColor: COLORS.D_FUNCTION,
        lightColor: COLORS.L_FUNCTION,
        darkColorBg: COLORS.D_BACKGROUND_BOLD,
        lightColorBg: COLORS.L_BACKGROUND_BOLD,
        intensityBg: Intensity.MEDIUM
    }),
    [Style.EDITOR_HEADING_3]: Object.freeze({
        intensity: Intensity.HIGH,
        isBold: true,
        isItalic: false,
        isStrikethrough: false,
        isUnderline: false,
        darkColor: COLORS.D_KEYWORD,        // Lila wie Keywords
        lightColor: COLORS.L_KEYWORD,
        darkColorBg: COLORS.D_BACKGROUND_BOLD,
        lightColorBg: COLORS.L_BACKGROUND_BOLD,
        intensityBg: Intensity.MEDIUM
    }),
    [Style.EDITOR_HEADING_4]: Object.freeze({
        intensity: Intensity.HIGH,
        isBold: true,
        isItalic: false,
        isStrikethrough: false,
        isUnderline: false,
        darkColor: COLORS.D_KEYWORD,
        lightColor: COLORS.L_KEYWORD,
        darkColorBg: COLORS.D_BACKGROUND_BOLD,
        lightColorBg: COLORS.L_BACKGROUND_BOLD,
        intensityBg: Intensity.MEDIUM
    }),
    [Style.EDITOR_HEADING_5]: Object.freeze({
        intensity: Intensity.MEDIUM,
        isBold: true,
        isItalic: false,
        isStrikethrough: false,
        isUnderline: false,
        darkColor: COLORS.D_TYPE,           // Gelb wie Types
        lightColor: COLORS.L_TYPE,
        darkColorBg: COLORS.D_BACKGROUND,
        lightColorBg: COLORS.L_BACKGROUND,
        intensityBg: Intensity.LOW
    }),
    [Style.EDITOR_HEADING_6]: Object.freeze({
        intensity: Intensity.MEDIUM,
        isBold: true,
        isItalic: false,
        isStrikethrough: false,
        isUnderline: false,
        darkColor: COLORS.D_TYPE,
        lightColor: COLORS.L_TYPE,
        darkColorBg: COLORS.D_BACKGROUND,
        lightColorBg: COLORS.L_BACKGROUND,
        intensityBg: Intensity.LOW
    }),
    [Style.EDITOR_LINK]: Object.freeze({
        intensity: Intensity.MEDIUM,
        isBold: false,
        isItalic: false,
        isStrikethrough: false,
        isUnderline: true,
        darkColor: COLORS.D_STRING,         // Grün wie Strings
        lightColor: COLORS.L_STRING,
        darkColorBg: COLORS.D_BACKGROUND,
        lightColorBg: COLORS.L_BACKGROUND,
        intensityBg: Intensity.LOW
    }),
    [Style.EDITOR_LINK_BROKEN]: Object.freeze({
        intensity: Intensity.LOW,
        isBold: false,
        isItalic: false,
        isStrikethrough: true,
        isUnderline: true,
        darkColor: COLORS.D_COMMENT,        // Gedämpft (grau)
        lightColor: COLORS.L_COMMENT,
        darkColorBg: COLORS.D_BACKGROUND_DIM,
        lightColorBg: COLORS.L_BACKGROUND_DIM,
        intensityBg: Intensity.LOW
    }),
    [Style.EDITOR_MARKER]: Object.freeze({
        intensity: Intensity.LOW,
        isBold: false,
        isItalic: false,
        isStrikethrough: false,
        isUnderline: false,
        darkColor: COLORS.D_BORDER,         // Gleiche Farbe wie Pane-Rahmen
        lightColor: COLORS.L_BORDER,
        darkColorBg: COLORS.D_BACKGROUND_DIM,
        lightColorBg: COLORS.L_BACKGROUND_DIM,
        intensityBg: Intensity.LOW
    }),
    [Style.EDITOR_PLACEHOLDER]: Object.freeze({
        // Empty-State-Hint im Memory-Editor (Spec §9). Geist-Layer ueber dem
        // leeren Modell — gedimmt + kursiv, sodass User sofort sieht, dass es
        // sich nicht um echten Inhalt handelt.
        intensity: Intensity.LOW,
        isBold: false,
        isItalic: true,
        isStrikethrough: false,
        isUnderline: false,
        darkColor: COLORS.D_COMMENT,
        lightColor: COLORS.L_COMMENT
    }),
    [Style.EDITOR_MARKER_DIM]: Object.freeze({
        // Geometrie identisch zu EDITOR_MARKER; Farbe wird im Renderer
        // halb in Richtung Pane-Background gemixt (mode-unabhaengig).
        intensity: Intensity.LOW,
        isBold: false,
        isItalic: false,
        isStrikethrough: false,
        isUnderline: false,
        darkColor: COLORS.D_BORDER,
        lightColor: COLORS.L_BORDER,
        darkColorBg: COLORS.D_BACKGROUND_DIM,
        lightColorBg: COLORS.L_BACKGROUND_DIM,
        intensityBg: Intensity.LOW,
        dimToBackground: 0.5                // Anteil Background-Mix im Renderer
    }),
    [Style.EDITOR_BLOCKQUOTE]: Object.freeze({
        intensity: Intensity.LOW,
        isBold: false,
        isItalic: true,
        isStrikethrough: false,
        isUnderline: false,
        darkColor: COLORS.D_COMMENT,        // Grau, kursiv
        lightColor: COLORS.L_COMMENT,
        darkColorBg: COLORS.D_BACKGROUND_DIM,
        lightColorBg: COLORS.L_BACKGROUND_DIM,
        intensityBg: Intensity.LOW
    }),
    [Style.EDITOR_BLOCKQUOTE_UNITALIC]: Object.freeze({
        // Kontrast-Variante: in einem Blockquote (kursiv) wird *italic* als
        // nicht-kursiv gerendert — sonst wäre der Italic-Marker visuell ein No-Op.
        intensity: Intensity.LOW,
        isBold: false,
        isItalic: false,
        isStrikethrough: false,
        isUnderline: false,
        darkColor: COLORS.D_COMMENT,
        lightColor: COLORS.L_COMMENT,
        darkColorBg: COLORS.D_BACKGROUND_DIM,
        lightColorBg: COLORS.L_BACKGROUND_DIM,
        intensityBg: Intensity.LOW
    }),
    [Style.EDITOR_HR]: Object.freeze({
        intensity: Intensity.LOW,
        isBold: false,
        isItalic: false,
        isStrikethrough: false,
        isUnderline: false,
        darkColor: COLORS.D_BORDER,         // Rahmenfarbe
        lightColor: COLORS.L_BORDER,
        darkColorBg: COLORS.D_BACKGROUND_DIM,
        lightColorBg: COLORS.L_BACKGROUND_DIM,
        intensityBg: Intensity.LOW
    }),
    [Style.SEPARATOR_TITLE]: Object.freeze({
        intensity: Intensity.MEDIUM,
        isBold: true,
        isItalic: false,
        isStrikethrough: false,
        isUnderline: false,
        darkColor: COLORS.D_TEXT,           // Normale Textfarbe (bleibt bold, hebt die Section-Überschrift ab)
        lightColor: COLORS.L_TEXT,
        darkColorBg: COLORS.D_BACKGROUND,
        lightColorBg: COLORS.L_BACKGROUND,
        intensityBg: Intensity.LOW
    }),
    [Style.EDITOR_CODE]: Object.freeze({
        intensity: Intensity.MEDIUM,
        isBold: false,
        isItalic: false,
        isStrikethrough: false,
        isUnderline: false,
        isMono: true,                       // Monospace-Font erzwingen (analog CONSOLE_AI_CODE)
        darkColor: COLORS.D_TYPE,           // Gelb wie Types
        lightColor: COLORS.L_TYPE
    }),

    // Selection-Styles (mit Background-Farben)
    [Style.EDITOR_SELECTION]: Object.freeze({
        intensity: Intensity.MEDIUM,
        isBold: false,
        isItalic: false,
        isStrikethrough: false,
        isUnderline: false,
        darkColor: COLORS.D_TEXT,
        lightColor: COLORS.L_TEXT,
        // Selection-Hintergrund je nach Mode
        darkBackground: COLORS.D_SELECTION,
        lightBackground: COLORS.L_SELECTION,
        nerdBackground: '#33cc33',          // Nerd: Grün als Hintergrund (invertiert)
        nerdForeground: '#000000'           // Nerd: Schwarz als Vordergrund (invertiert)
    }),
    [Style.EDITOR_CURSOR]: Object.freeze({
        intensity: Intensity.HIGH,
        isBold: false,
        isItalic: false,
        isStrikethrough: false,
        isUnderline: false,
        // Cursor als Block: Hintergrund ist die Cursor-Farbe, Vordergrund ist Hintergrund-Farbe
        darkColor: '#000033',               // Text auf Cursor: Hintergrundfarbe Dark Colored
        lightColor: '#ffffff',              // Text auf Cursor: Weiß
        darkBackground: COLORS.D_ACCENT,    // Cursor-Block: VS Code Blau
        lightBackground: COLORS.L_ACCENT,   // Cursor-Block: Dunkelblau
        nerdBackground: '#33cc33',          // Nerd: Phosphor-Grün
        nerdForeground: '#000000'           // Nerd: Schwarz auf Grün
    }),

    // Search-Styles (mit Background-Farben)
    [Style.SEARCH_MATCH]: Object.freeze({
        intensity: Intensity.MEDIUM,
        isBold: false,
        isItalic: false,
        isStrikethrough: false,
        isUnderline: false,
        darkColor: COLORS.D_TEXT,
        lightColor: COLORS.L_TEXT,
        darkBackground: '#3a3a00',
        lightBackground: '#ffffcc'
    }),
    [Style.SEARCH_CURRENT_MATCH]: Object.freeze({
        intensity: Intensity.HIGH,
        isBold: false,
        isItalic: false,
        isStrikethrough: false,
        isUnderline: false,
        darkColor: COLORS.D_TEXT_BOLD,
        lightColor: COLORS.L_TEXT_BOLD,
        darkBackground: '#806600',
        lightBackground: '#ffdd00'
    }),

    // Scrollbar-Styles
    [Style.SCROLLBAR_TRACK]: Object.freeze({
        intensity: Intensity.LOW,
        isBold: false,
        isItalic: false,
        isStrikethrough: false,
        isUnderline: false,
        darkColor: COLORS.D_BORDER,
        lightColor: COLORS.L_BORDER
    }),
    [Style.SCROLLBAR_THUMB]: Object.freeze({
        intensity: Intensity.MEDIUM,
        isBold: false,
        isItalic: false,
        isStrikethrough: false,
        isUnderline: false,
        darkColor: COLORS.D_OPERATOR,       // Cyan
        lightColor: COLORS.L_OPERATOR
    }),

    // Outline-Styles
    [Style.OUTLINE_CONNECTOR]: Object.freeze({
        intensity: Intensity.LOW,
        isBold: false,
        isItalic: false,
        isStrikethrough: false,
        isUnderline: false,
        darkColor: COLORS.D_COMMENT,        // Gedämpftes Grau
        lightColor: COLORS.L_COMMENT
        // Bewusst KEINE +BG-Variante: Linien (auch im Background-Bereich)
        // tragen dieselbe Farbe wie im normalen Dokument — nur Text ist sepia.
    }),
    [Style.OUTLINE_CURRENT]: Object.freeze({
        intensity: Intensity.HIGH,
        isBold: true,
        isItalic: false,
        isStrikethrough: false,
        isUnderline: false,
        darkColor: COLORS.D_ACCENT,         // VS Code Blau (Active-Border-Color)
        lightColor: COLORS.L_ACCENT
        // Bewusst KEINE +BG-Variante: aktive Linie/Icon-Border tragen auch im
        // Background-Bereich dieselbe Active-Border-Color wie im normalen Dokument.
    }),
    [Style.OUTLINE_NOTE]: Object.freeze({
        intensity: Intensity.MEDIUM,
        isBold: false,
        isItalic: false,
        isStrikethrough: false,
        isUnderline: false,
        darkColor: COLORS.D_TEXT,
        lightColor: COLORS.L_TEXT,
        darkColorBg: COLORS.D_BACKGROUND,
        lightColorBg: COLORS.L_BACKGROUND,
        intensityBg: Intensity.LOW
    }),

    // Console-Styles (Core Pane)
    [Style.CONSOLE_THINKING]: Object.freeze({
        intensity: Intensity.LOW,
        isBold: false,
        isItalic: true,
        isStrikethrough: false,
        isUnderline: false,
        darkColor: COLORS.D_COMMENT,
        lightColor: COLORS.L_COMMENT
    }),
    [Style.CONSOLE_THINKING_COLLAPSED]: Object.freeze({
        intensity: Intensity.LOW,
        isBold: false,
        isItalic: false,
        isStrikethrough: false,
        isUnderline: false,
        darkColor: COLORS.D_COMMENT,
        lightColor: COLORS.L_COMMENT
    }),
    [Style.CONSOLE_SYSTEM]: Object.freeze({
        intensity: Intensity.LOW,
        isBold: false,
        isItalic: false,
        isStrikethrough: false,
        isUnderline: false,
        darkColor: COLORS.D_COMMENT,
        lightColor: COLORS.L_COMMENT
    }),
    [Style.CONSOLE_REVIEW_PANEL]: Object.freeze({
        intensity: Intensity.HIGH,
        isBold: false,
        isItalic: false,
        isStrikethrough: false,
        isUnderline: false,
        darkColor: COLORS.D_STRING,
        lightColor: COLORS.L_STRING
    }),
    [Style.CONSOLE_USER]: Object.freeze({
        intensity: Intensity.HIGH,
        isBold: false,
        isItalic: false,
        isStrikethrough: false,
        isUnderline: false,
        darkColor: COLORS.D_TEXT,
        lightColor: COLORS.L_TEXT
    }),
    [Style.CONSOLE_AI]: Object.freeze({
        intensity: Intensity.MEDIUM,
        isBold: false,
        isItalic: false,
        isStrikethrough: false,
        isUnderline: false,
        darkColor: COLORS.D_STRING,
        lightColor: COLORS.L_STRING
    }),
    [Style.CONSOLE_ERROR]: Object.freeze({
        intensity: Intensity.HIGH,
        isBold: false,
        isItalic: false,
        isStrikethrough: false,
        isUnderline: false,
        darkColor: COLORS.D_VARIABLE,
        lightColor: COLORS.L_VARIABLE
    }),
    [Style.CONSOLE_PROMPT]: Object.freeze({
        intensity: Intensity.HIGH,
        isBold: false,
        isItalic: false,
        isStrikethrough: false,
        isUnderline: false,
        darkColor: COLORS.D_ACCENT,
        lightColor: COLORS.L_ACCENT
    }),
    [Style.CONSOLE_INPUT]: Object.freeze({
        intensity: Intensity.HIGH,
        isBold: false,
        isItalic: false,
        isStrikethrough: false,
        isUnderline: false,
        darkColor: COLORS.D_TEXT,
        lightColor: COLORS.L_TEXT
    }),
    [Style.CONSOLE_LOCK]: Object.freeze({
        intensity: Intensity.LOW,
        isBold: false,
        isItalic: false,
        isStrikethrough: false,
        isUnderline: false,
        darkColor: COLORS.D_COMMENT,
        lightColor: COLORS.L_COMMENT
    }),

    // Console AI Block-Styles
    [Style.CONSOLE_AI_HEADING]: Object.freeze({
        intensity: Intensity.HIGH,
        isBold: true,
        isItalic: false,
        isStrikethrough: false,
        isUnderline: false,
        darkColor: COLORS.D_STRING,
        lightColor: COLORS.L_STRING
    }),
    [Style.CONSOLE_AI_CODE]: Object.freeze({
        // Farbe identisch zu EDITOR_CODE (D_TYPE/L_TYPE), damit Inline-Code in
        // Console-Eingabe und AI-Ausgabe konsistent zum Editor wirkt.
        intensity: Intensity.MEDIUM,
        isBold: false,
        isItalic: false,
        isStrikethrough: false,
        isUnderline: false,
        isMono: true,   // Monospace-Font erzwingen (Inline-Code, Code-Blöcke)
        darkColor: COLORS.D_TYPE,
        lightColor: COLORS.L_TYPE
    }),
    [Style.CONSOLE_AI_BLOCKQUOTE]: Object.freeze({
        intensity: Intensity.MEDIUM,
        isBold: false,
        isItalic: true,
        isStrikethrough: false,
        isUnderline: false,
        darkColor: COLORS.D_STRING,
        lightColor: COLORS.L_STRING
    }),
    [Style.CONSOLE_AI_BLOCKQUOTE_UNITALIC]: Object.freeze({
        // Kontrast-Variante in der Console — analog EDITOR_BLOCKQUOTE_UNITALIC.
        intensity: Intensity.MEDIUM,
        isBold: false,
        isItalic: false,
        isStrikethrough: false,
        isUnderline: false,
        darkColor: COLORS.D_STRING,
        lightColor: COLORS.L_STRING
    }),

    // Klickbare URL-Links (heller als AI-Text, Underline)
    [Style.CONSOLE_LINK]: Object.freeze({
        intensity: Intensity.HIGH,
        isBold: false,
        isItalic: false,
        isStrikethrough: false,
        isUnderline: true,
        darkColor: COLORS.D_FUNCTION,
        lightColor: COLORS.L_FUNCTION
    })
})

// === Font-Familien (vor Display Modes, da dort referenziert) ===
export const FontCategory = Object.freeze({
    MONOSPACE: 'MONOSPACE',
    PROPORTIONAL: 'PROPORTIONAL'
})

export const FontFamily = Object.freeze({
    DINA: 'DINA',
    NOTO: 'NOTO',
    NOTO_PROP: 'NOTO_PROP',
    GARAMOND: 'GARAMOND'
})

// === Display Modes ===
// Jeder Mode definiert Vordergrund (3 Stufen), Hintergrund, Glow, Cursor und Default-Font
export const DisplayMode = Object.freeze({
    ULTRA_NERD: 'ULTRA_NERD',
    REGULAR_NERD: 'REGULAR_NERD',
    MODERN_NERD: 'MODERN_NERD',
    WORKER: 'WORKER',
    WORKER_DARK: 'WORKER_DARK',
    ARTIST: 'ARTIST',
    ARTIST_DARK: 'ARTIST_DARK'
})

export const DISPLAY_MODES = Object.freeze({
    [DisplayMode.ULTRA_NERD]: Object.freeze({
        name: 'Ultra Nerd',
        description: 'Dina font, phosphor green',
        font: FontFamily.DINA,
        isColored: false,
        isNerd: true,
        background: '#000000',
        activeRowBackground: '#001a00',     // Schwacher Phosphor-Tint
        foreground: Object.freeze({
            [Intensity.LOW]: '#1a6b1a',
            [Intensity.MEDIUM]: '#33cc33',
            [Intensity.HIGH]: '#66ff66'
        }),
        glow: Object.freeze({ color: '#33ff33', blur: 4 }),
        cursor: '#33cc33'
    }),

    [DisplayMode.REGULAR_NERD]: Object.freeze({
        name: 'Regular Nerd',
        description: 'Noto Mono, phosphor green',
        font: FontFamily.NOTO,
        isColored: false,
        isNerd: true,
        background: '#000000',
        activeRowBackground: '#001a00',     // Schwacher Phosphor-Tint
        foreground: Object.freeze({
            [Intensity.LOW]: '#1a6b1a',
            [Intensity.MEDIUM]: '#33cc33',
            [Intensity.HIGH]: '#66ff66'
        }),
        glow: Object.freeze({ color: '#33ff33', blur: 4 }),
        cursor: '#33cc33'
    }),

    [DisplayMode.MODERN_NERD]: Object.freeze({
        name: 'Modern Nerd',
        description: 'Noto Mono, colored on dark',
        font: FontFamily.NOTO,
        isColored: true,
        isDarkMode: true,
        background: '#000033',
        activeRowBackground: '#0a0a4a',     // Leicht heller blau
        foreground: Object.freeze({
            [Intensity.LOW]: '#6666aa',
            [Intensity.MEDIUM]: '#aaaaff',
            [Intensity.HIGH]: '#ffffff'
        }),
        glow: Object.freeze({ color: '#aaaaff', blur: 2 }),
        cursor: '#ffffff'
    }),

    [DisplayMode.WORKER]: Object.freeze({
        name: 'Worker',
        description: 'Noto Sans, colored on white',
        font: FontFamily.NOTO_PROP,
        isColored: true,
        isDarkMode: false,
        background: '#ffffff',
        activeRowBackground: '#f0f0fa',     // Leicht bluish off-white
        foreground: Object.freeze({
            [Intensity.LOW]: '#8888bb',
            [Intensity.MEDIUM]: '#4444aa',
            [Intensity.HIGH]: '#000066'
        }),
        glow: Object.freeze({ color: null, blur: 0 }),
        cursor: '#000066'
    }),

    [DisplayMode.WORKER_DARK]: Object.freeze({
        name: 'Worker Dark',
        description: 'Noto Sans, colored on dark',
        font: FontFamily.NOTO_PROP,
        isColored: true,
        isDarkMode: true,
        background: '#060628',
        activeRowBackground: '#0f0f40',     // Leicht heller, etwas weniger blau
        foreground: Object.freeze({
            [Intensity.LOW]: '#6666aa',
            [Intensity.MEDIUM]: '#aaaaff',
            [Intensity.HIGH]: '#ffffff'
        }),
        glow: Object.freeze({ color: '#aaaaff', blur: 2 }),
        cursor: '#ffffff'
    }),

    [DisplayMode.ARTIST]: Object.freeze({
        name: 'Artist',
        description: 'EB Garamond, monochrome light',
        font: FontFamily.GARAMOND,
        isColored: false,
        background: '#ffffff',
        activeRowBackground: '#f5f3ed',     // Leicht waermerer off-white (Papier)
        foreground: Object.freeze({
            [Intensity.LOW]: '#999999',
            [Intensity.MEDIUM]: '#555555',
            [Intensity.HIGH]: '#000000'
        }),
        glow: Object.freeze({ color: null, blur: 0 }),
        cursor: '#000000'
    }),

    [DisplayMode.ARTIST_DARK]: Object.freeze({
        name: 'Artist Dark',
        description: 'EB Garamond, monochrome dark',
        font: FontFamily.GARAMOND,
        isColored: false,
        background: '#000000',
        activeRowBackground: '#141414',     // Sehr leicht heller (~8%)
        foreground: Object.freeze({
            [Intensity.LOW]: '#666666',
            [Intensity.MEDIUM]: '#aaaaaa',
            [Intensity.HIGH]: '#ffffff'
        }),
        glow: Object.freeze({ color: '#ffffff', blur: 2 }),
        cursor: '#ffffff'
    })
})

// === Aktueller Display Mode (wird zur Laufzeit geändert) ===
export const DEFAULT_DISPLAY_MODE = DisplayMode.WORKER_DARK

// === Box-Drawing Characters ===
// Werden vom Renderer als Linien gezeichnet, nicht als Font-Glyphen
export const BoxChars = Object.freeze({
    // Einfache Linien (für Menüs)
    SINGLE: Object.freeze({
        TOP_LEFT: '┌',
        TOP_RIGHT: '┐',
        BOTTOM_LEFT: '└',
        BOTTOM_RIGHT: '┘',
        HORIZONTAL: '─',
        VERTICAL: '│',
        T_DOWN: '┬',
        T_UP: '┴',
        T_RIGHT: '├',
        T_LEFT: '┤',
        CROSS: '┼'
    }),

    // Doppelte Linien (für wichtige Dialoge wie Copyright)
    DOUBLE: Object.freeze({
        TOP_LEFT: '╔',
        TOP_RIGHT: '╗',
        BOTTOM_LEFT: '╚',
        BOTTOM_RIGHT: '╝',
        HORIZONTAL: '═',
        VERTICAL: '║',
        T_DOWN: '╦',
        T_UP: '╩',
        T_RIGHT: '╠',
        T_LEFT: '╣',
        CROSS: '╬'
    })
})

// Set aller Box-Drawing Characters für schnelle Erkennung im Renderer
export const BOX_CHARS_SET = new Set([
    ...Object.values(BoxChars.SINGLE),
    ...Object.values(BoxChars.DOUBLE)
])

// Set nur der doppelten Box-Drawing Characters (für Linienbreiten-Unterscheidung)
export const DOUBLE_BOX_CHARS_SET = new Set(Object.values(BoxChars.DOUBLE))

// === Font-Einstellungen ===
export const FONTS = Object.freeze({
    [FontFamily.DINA]: Object.freeze({
        name: 'Dina Remaster II',
        category: FontCategory.MONOSPACE,
        lineHeightFactor: 1.2,
        family: 'DinaRemasterII',
        familyBold: 'DinaRemasterII',
        familyItalic: null,
        familyBoldItalic: null
    }),
    [FontFamily.NOTO]: Object.freeze({
        name: 'Noto Sans Mono',
        category: FontCategory.MONOSPACE,
        lineHeightFactor: 1.2,
        family: 'NotoSansMono',
        familyBold: 'NotoSansMono',
        familyItalic: null,
        familyBoldItalic: null
    }),
    [FontFamily.NOTO_PROP]: Object.freeze({
        name: 'Noto Sans',
        category: FontCategory.PROPORTIONAL,
        lineHeightFactor: 1.4,
        // Grid-Rendering (Borders, Editor, Console) bleibt monospaced
        gridFamily: 'NotoSansMono',
        gridFamilyBold: 'NotoSansMono',
        // Proportionales Rendering (Context-Pane Text)
        family: 'NotoSans',
        familyBold: 'NotoSans',
        familyItalic: 'NotoSansItalic',
        familyBoldItalic: 'NotoSansItalic',
        kerning: { delta: 0.6 }
    }),
    [FontFamily.GARAMOND]: Object.freeze({
        name: 'EB Garamond',
        category: FontCategory.PROPORTIONAL,
        lineHeightFactor: 1.4,
        // Grid bleibt monospaced (gleicher Mono-Font wie Noto Sans)
        gridFamily: 'NotoSansMono',
        gridFamilyBold: 'NotoSansMono',
        // Proportionales Rendering mit Serifen-Font (Body-Text, heightQ >= 6)
        family: 'EBGaramond',
        familyBold: 'EBGaramond',
        familyItalic: 'EBGaramondItalic',
        familyBoldItalic: 'EBGaramondItalic',
        // Kerning-Overrides: Serifen-Font braucht aggressivere Nose-Korrektur
        kerning: { stepFactor: 0.20, capsNose: -0.33, lowerNose: -0.30, delta: 0, barThreshold: 0.80 },
        // Oberlängen-Korrektur: Garamond hat kürzere x-Höhe → Default-Faktor 0.85
        // lässt die Glyphen optisch zu tief sitzen; 0.77 hebt sie sauber an.
        baselineFactor: 0.77,
        // System-Font fuer UI-Elemente (Labels, Menus, Console) bei 4QL-Hoehe
        systemFont: FontFamily.NOTO_PROP
    })
})

// Default-Font leitet sich vom Default-Display-Mode ab
export const DEFAULT_FONT = DISPLAY_MODES[DEFAULT_DISPLAY_MODE].font

export const FONT_SIZE = Object.freeze({
    default: 20,
    min: 10,
    max: 38,
    step: 2
})

// Zeilenhöhe als Faktor der Schriftgröße
export const LINE_HEIGHT_FACTOR = 1.1

// === Timing-Konstanten ===
export const TIMING = Object.freeze({
    cursorBlink: 500,
    doubleTabThreshold: 300,
    doubleClickThreshold: 300,
    scrollPixelsPerLine: 40,
    scrollMaxLinesPerEvent: 20,
    // Touch-Inertia (iOS-Style): exponentieller Geschwindigkeits-Decay
    // pro 60fps-Frame. 0.95 → ~1.2–1.6 s Ausklingen; niedriger = schnellerer Stopp.
    inertiaDecayPerFrame: 0.95,
    // Schwellwert in px/ms, unter dem Inertia stoppt (vermeidet endloses Schleifen)
    inertiaMinVelocity: 0.05,
    // Zeitfenster für gleitenden Velocity-Mittelwert am Finger-Release
    inertiaVelocitySampleWindowMs: 60
})

// === AI-Agent (Client-Transport) ===
// Ack-Robustheit fuer Auto-Apply-Verdicts: Geht der action-done-POST verloren
// (429/5xx/Netzwerkfehler), wartet der Server 5 Minuten auf den Ack
// (action_timeout) und haelt den laengst angewendeten Block faelschlich fuer
// nicht angewendet — der Agent legt dann Duplikate an (Incident 2026-07-14,
// »JUNG BLEIBEN«). Der Ack-POST ist idempotent (Server resolved nur ein
// pending Promise), Retries sind daher risikolos.
export const AGENT = Object.freeze({
    actionDoneAttempts: 2,
    actionDoneRetryDelayMs: 1000
})

// === Touch-Optimierung (iPad / iPhone) ===
// Slop = wie weit darf der Finger vom intendierten Ziel abweichen, gemessen
// in Char-Einheiten (Spalten × Zeilen). Bei mehreren Treffern gewinnt der
// nähestliegende (euklidische Distanz). Maus-Hit-Tests bleiben pixelgenau.
export const TOUCH = Object.freeze({
    slop: Object.freeze({
        borderControl:     Object.freeze({ dx: 2, dy: 1 }),
        borderControlProp: Object.freeze({ dx: 3, dy: 1 }),  // Proportional-Mode
        separatorControl:  Object.freeze({ dx: 2, dy: 1 }),
        tabBar:            Object.freeze({ dx: 1, dy: 1 }),
        selectionHandle:   Object.freeze({ dx: 3, dy: 1 })   // Phase 5
    }),
    // Phase 1: Zwei-Finger-Pinch als Font-Zoom (analog Trackpad-Pinch).
    // Distanz-Verhältnis (aktuell/start), ab dem ein Zoom-Step gefeuert wird.
    pinchStepThreshold: 1.2,
    // Phase 2: Tap auf y=0..(height-1) blendet die Tab-Leiste ein (wenn kein
    // Border-Control im Slop liegt). Hoehe in Zeilen.
    tabBarTrigger: Object.freeze({ height: 2 }),
    // Phase 3
    longPressDurationMs: 500,
    // Phase 5: Selection-Handle-Drag — Auto-Scroll am Viewport-Rand, solange
    // der Finger waehrend des Drags in der Rand-Zone bleibt.
    autoScroll: Object.freeze({
        edgeZoneRows: 2,      // Rand-Zone-Hoehe in Zeilen (oben + unten je)
        intervalMs:   50      // 1 Zeile pro 50 ms (= ~20 Zeilen/s)
    }),
    // Phase 6: verstecktes <textarea>, das die System-Tastatur-Eingaben fuer
    // iPad / iPhone einsammelt. Sentinel ist ein Zero-Width-Space, damit
    // Backspace bei leerem Inhalt trotzdem ein beforeinput-Event mit
    // deleteContentBackward feuert.
    keyboardSink: Object.freeze({
        elementId:    'touch-keyboard-sink',
        sentinelChar: '​'
    })
})

// === Diktat-Konstanten ===
export const DICTATION = Object.freeze({
    doubleCtrlTimeout: 400,     // ms für Doppel-Ctrl-Erkennung
    model: 'nova-3',            // Deepgram-Modell (beste Deutsch-Qualität)
    language: 'multi',          // Automatische Spracherkennung (DE/EN Code-Switching)
    sampleRate: 16000,          // Audio-Samplerate für Deepgram (16kHz)
    utteranceEndMs: 0,           // 0 = deaktiviert (kein automatischer Umbruch bei Stille)
    endpointingMs: 300,         // Stille für Satz-Ende (ms)
    tokenEndpoint: '/api/dictation/token',
    usageEndpoint: '/api/dictation/usage'
})

// === Command-Palette Shortcuts ===
export const COMMAND_SHORTCUTS = Object.freeze([
    { key: 'P', modifiers: ['ctrl', 'shift'] },
    { key: 'P', modifiers: ['meta', 'shift'] },
    { key: 'K', modifiers: ['ctrl'] },
    { key: 'K', modifiers: ['meta'] },
    { key: 'F1', modifiers: [] }
])

// === Menü-Struktur ===
// Deklarative Definition aller Menüs und Untermenüs
export const MENU_STRUCTURE = Object.freeze({
    id: 'main',
    title: 'CARBISDALE',
    items: [
        { key: 'P', label: 'Projects', action: 'openDashboard' },
        {
            key: 'F',
            label: 'File',
            submenu: {
                id: 'file',
                title: 'FILE',
                items: [
                    { key: 'C', label: 'Clear Content', action: 'fileClearContent' },
                    { key: 'I', label: 'Import (.txt, .md)', action: 'fileImport' },
                    { key: 'L', label: 'Load (.cbl)', action: 'fileLoad' },
                    { key: 'S', label: 'Save (.cbl)', action: 'fileSave' },
                    { key: 'E', label: 'Export (.md)', action: 'fileExport' }
                ]
            }
        },
        {
            key: 'M',
            label: 'Project Statistics',
            submenu: {
                id: 'projectStatistics',
                title: 'PROJECT STATISTICS',
                isDynamic: true,  // Items werden zur Laufzeit generiert
                items: [
                    { key: '1', label: 'Paragraphs:     0', isInfo: true, infoKey: 'paragraphs' },
                    { key: '2', label: 'Words:          0', isInfo: true, infoKey: 'words' },
                    { key: '3', label: 'Characters:     0', isInfo: true, infoKey: 'charsWithSpaces' },
                    { key: '4', label: 'w/o spaces:     0', isInfo: true, infoKey: 'charsWithoutSpaces' },
                    { key: '5', label: 'Created:        -', isInfo: true, infoKey: 'created' },
                    { key: '6', label: 'Modified:       -', isInfo: true, infoKey: 'modified' }
                ]
            }
        },
        {
            key: 'D',
            label: 'Display Mode',
            submenu: {
                id: 'display',
                title: 'DISPLAY MODE',
                items: [
                    { key: 'U', label: 'Ultra Nerd', action: 'setDisplayMode', value: DisplayMode.ULTRA_NERD },
                    { key: 'R', label: 'Regular Nerd', action: 'setDisplayMode', value: DisplayMode.REGULAR_NERD },
                    { key: 'M', label: 'Modern Nerd', action: 'setDisplayMode', value: DisplayMode.MODERN_NERD },
                    { key: '-', isDivider: true },
                    { key: 'W', label: 'Worker', action: 'setDisplayMode', value: DisplayMode.WORKER },
                    { key: 'K', label: 'Worker Dark', action: 'setDisplayMode', value: DisplayMode.WORKER_DARK },
                    { key: '-', isDivider: true },
                    { key: 'A', label: 'Artist', action: 'setDisplayMode', value: DisplayMode.ARTIST },
                    { key: 'T', label: 'Artist Dark', action: 'setDisplayMode', value: DisplayMode.ARTIST_DARK }
                ]
            }
        },
        {
            key: 'V',
            label: 'View',
            submenu: {
                id: 'view',
                title: 'VIEW',
                items: [
                    { key: '+', label: 'Zoom In',    action: 'zoom', value: 'in' },
                    { key: '-', label: 'Zoom Out',   action: 'zoom', value: 'out' },
                    { key: '0', label: 'Reset Zoom', action: 'zoom', value: 'reset' },
                    { key: 'F', label: 'Fullscreen', action: 'toggleFullscreen' }
                ]
            }
        },
        {
            key: 'S',
            label: 'Spellcheck',
            submenu: {
                id: 'spellcheck',
                title: 'SPELLCHECK',
                items: [
                    { key: 'O', label: 'Off',     action: 'setSpellcheckLang', value: null },
                    { key: 'D', label: 'Deutsch', action: 'setSpellcheckLang', value: 'de' },
                    { key: 'E', label: 'English', action: 'setSpellcheckLang', value: 'en' },
                    {
                        key: 'F',
                        label: 'Fallback dictionary',
                        labelAccessor: 'getSpellcheckFallbackLabel',
                        action: 'toggleSpellcheckFallback',
                        valueAccessor: 'getSpellcheckFallback'
                    },
                    { key: 'P', label: 'Edit personal dictionary…', action: 'editPersonalDict' }
                ]
            }
        },
        {
            key: 'U',
            label: 'Manual',
            action: 'openManual'
        },
        {
            key: 'R',
            label: 'Reference',
            action: 'openReference'
        },
        {
            key: 'P',
            label: 'Privacy & Legal',
            action: 'openLegal'
        },
        {
            key: 'A',
            label: 'About',
            action: 'showAbout'
        }
    ]
})

// === Copyright / Help Info ===
export const APP_INFO = Object.freeze({
    name: 'Carbisdale',
    subtitle: 'A Writelite Editor',
    copyright: '(c) Boris Baginski 1988-2026'
})

// === Dummy-Content für initiales Füllen ===
export const DUMMY_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'

// === Initiale Cursor-Position ===
export const INITIAL_CURSOR = Object.freeze({
    x: 10,
    y: 10
})

// === Bereichs-Zustände ===
export const PaneState = Object.freeze({
    COLLAPSED: 'COLLAPSED',     // Eingeklappt (nur Linie mit +)
    NORMAL: 'NORMAL',           // Normal ausgeklappt
    FULLSCREEN: 'FULLSCREEN',   // Flächendeckend
    HIDDEN: 'HIDDEN'            // Komplett unsichtbar (Memory-Pane Default)
})

// === Bereichs-IDs ===
export const PaneId = Object.freeze({
    OUTLINE: 'OUTLINE',
    MAIN: 'MAIN',
    CONTEXT: 'CONTEXT',
    CORE: 'CORE',
    MEMORY: 'MEMORY',           // AI-Memory-Editor (Spec §6 Phase 4)
    PERSONAL_DICT: 'PERSONAL_DICT' // Personal-Dictionary-Editor (Spellcheck §10 Phase 4)
})

// === Console-Fokus-Zustände ===
export const ConsoleFocus = Object.freeze({
    NONE: 'NONE',         // Konsole inaktiv, Tastatur an Editor
    HOVER: 'HOVER',       // Maus über Core, Cursor blinkt, Tastatur still an Console
    FOCUSED: 'FOCUSED'    // Konsole aktiv (getippt/geklickt), Border aktiv
})

// === Layout-Dimensionen (Prozent) ===
export const LAYOUT = Object.freeze({
    OUTLINE_WIDTH: 0.28,    // 28%
    MAIN_WIDTH: 0.44,       // 44%
    CONTEXT_WIDTH: 0.28,    // 28%
    CORE_HEIGHT: 0.30       // 30%
})

// === Editor-Konstanten ===
export const EDITOR = Object.freeze({
    MARKER_WIDTH: 1,        // Breite der Absatz-Marker (">", "<")
    SCROLLBAR_WIDTH: 1,     // Breite der Scrollbar
    MAX_SEPARATOR_DEPTH: 6,  // Maximale Gliederungstiefe für Separatoren (0-6)
    MAX_OUTLINE_ITEM_DEPTH: 10, // Maximale Tiefe für Outline-Items (0-10) — Single Source auch für die Agent-Schemas (server/agent/schemas.js)
    BACKGROUND_SEPARATOR_DEPTH: -1, // Spezielle Tiefe für den Background-Separator
    BACKGROUND_PADDING_LINES: 5,    // Leerzeilen vor dem Background-Separator
    LINK_PICKER_MAX_VISIBLE: 8,     // Sichtbare Items im Section-Link-Picker (Rest per Scroll)
    LINK_PICKER_WHEEL_SENSITIVITY: 0.6,  // Zeilen pro Mausrad-Notch (gedrosselt, sonst zu schnell)
    // Outline-(-)-Collapse-Icon vor Notes (nur Proportional): kleiner als die
    // Separator-(-)-Icons und um 1 Viertel tiefer, weil der Note-Text 4QL klein ist.
    OUTLINE_NOTE_ICON_SCALE: 0.75,       // 75 % der Standard-Control-Icon-Größe
    OUTLINE_NOTE_ICON_Y_OFFSET_Q: 1      // 1 Viertelzeile nach unten (Baseline-Feinschliff)
})

// Implicit-Head Section: Paragraphen vor dem ersten Separator im Main-Bereich.
// Sie hat keinen echten Separator, also keine UUID — der Agent adressiert sie
// ueber diesen reservierten Token (Underscore-Form, kein UUID-Format → keine
// Kollisionsgefahr). Spiegelung in server/config/constants.js IMPLICIT_HEAD_ID.
export const IMPLICIT_HEAD_ID = 'section_0'

// === Zeilenhöhen in Quarterlines (nur Proportional-Mode) ===
// Jede Zeile hat eine Höhe in Vierteln der Basis-charHeight (4 = Standard-Rasterhöhe)
export const LINE_HEIGHT_Q = Object.freeze({
    NORMAL: 6,        // Normaler Text, Listen, Blockquotes
    H1: 10,           // Heading 1
    H2: 9,            // Heading 2
    H3: 8,            // Heading 3
    H4: 7,            // Heading 4+ (H4, H5, H6)
    SEPARATOR: 4,     // Separatoren (Basis-Rasterhöhe)
    SECONDARY: 4,     // Notes/Info im Proportional — kleiner + System-Font (wie Separator-Labels)
    GRID: 4,          // Menüs, Overlays, Borders (unverändert)
    SYSTEM: 4,        // Console SYSTEM/ERROR Einträge
    THINKING: 4,      // Console Reasoning/Thinking
    CODE: 4,          // Code-Blöcke in Console (Mono-Font, kompakt = 1 Grid-Höhe)
    CODE_PARA: 4,     // Code-Block-Absatz im Editor (Mono, kompakt = 1 Grid-Höhe)
})

/**
 * Gibt die Zeilenhöhe in Quarterlines für ein Heading-Level zurück.
 * @param {number} level - Heading-Level (1-6)
 * @returns {number} Höhe in Quarterlines
 */
export function headingHeightQ(level) {
    if (level === 1) return LINE_HEIGHT_Q.H1
    if (level === 2) return LINE_HEIGHT_Q.H2
    if (level === 3) return LINE_HEIGHT_Q.H3
    return LINE_HEIGHT_Q.H4  // level >= 4
}

// === Minimap-Scrollbar (Proportional-Mode) ===
// Die Scrollbar-Spalte wird im Proportional-Mode als Minimap gerendert:
// pixelgenaue Section-Füllungen + Separator-Linien + Viewport-Underlayer.
// Alle Farben werden zur Render-Zeit aus dem aktuellen Style-System
// aufgelöst, hier stehen nur Alpha-Werte und Geometrie-Konstanten für
// leichtes Fine-Tuning.
export const MINIMAP = Object.freeze({
    MIN_VIEWPORT_HEIGHT: 24,         // Pixel — Drag-Handle muss mit der Maus greifbar bleiben
    INSET: 2,                        // Pixel pro Seite: Minimap-Inhalt schmaler als Spalte,
                                     // Viewport geht über die volle Breite und ragt beidseitig heraus

    VIEWPORT_ALPHA: 0.10,            // Sanfter Aufhellungs-Layer hinter den Sections
    VIEWPORT_EDGE_ALPHA: 0.45,       // Extra-Helligkeit für die beiden Überstand-Streifen (links/rechts),
                                     // damit der Viewport ohne Section-Overlay trotzdem deutlich sichtbar ist

    SECTION_EDITABLE_ALPHA: 0.22,    // Normale Sections (heller / weniger opak)
    SECTION_LOCKED_ALPHA:   0.42,    // Gelockte Sections (dunkler / opaker)
    SECTION_BG_ALPHA:       0.32,    // Background-Abschnitt (Sepia-Ton)

    SEPARATOR_ALPHA:        0.60,    // Separator-Linien (1 CSS-Pixel = 2 HW-Pixel auf Retina) —
                                     // stärker gedimmt als die Cursor-Linie (1 HW-Pixel, 100% Kontrast)
    BG_SEPARATOR_GAP:       1,       // Pixel-Gap zwischen Doppel-Linien des Background-Separators

    // Core-Console-Minimap: Entry-Typ-Farben (Alpha über Basis-Foreground)
    ENTRY_USER_ALPHA:       0.40,    // User-Eingaben
    ENTRY_AI_ALPHA:         0.28,    // AI-Antworten
    ENTRY_SYSTEM_ALPHA:     0.18,    // System-Messages
    ENTRY_ERROR_ALPHA:      0.55     // Error-Messages
})

// === Delta-Review (Canvas-Rendering im Main-Pane) ===
// Paragraphen, die NICHT Teil eines aktuellen Review-Vorschlags sind, werden
// stark abgedunkelt — damit klar ist, welcher Text zur Debatte steht. Die
// Werte sind bewusst konservativ gewaehlt: lesbar bleiben, aber deutlich
// zurueckgesetzt. An diesen Konstanten laesst sich spaeter fein justieren.
export const REVIEW = Object.freeze({
    DIM_ALPHA: 0.20   // globalAlpha fuer nicht-review Paragraphen (0 = unsichtbar, 1 = voll)
})

// === Absatzabstände (nur Proportional-Mode) ===
// Margin-Collapsing: effektiver Abstand = max(prev.after, next.before)
// Angabe in Viertel-Zeilenhöhen (1 QL ≈ 1/6 Normalzeile bei 6-QL-Text)
export const PARAGRAPH_SPACING = Object.freeze({
    // before/after pro Absatztyp — effektiv gilt max(prev.after, curr.before)
    NORMAL:     { before: 2, after: 2 },
    H1:         { before: 6, after: 3 },
    H2:         { before: 5, after: 3 },
    H3:         { before: 4, after: 3 },
    H4:         { before: 4, after: 2 },
    H5:         { before: 3, after: 2 },
    H6:         { before: 2, after: 2 },
    LIST:       { before: 0, after: 0 },  // Innerhalb einer Liste kein Abstand
    BLOCKQUOTE: { before: 2, after: 2 },
    HR:         { before: 2, after: 2 },
    SEPARATOR:  { before: 3, after: 3 },
    // Background-Separator: großer Abstand davor, der das 5-Zeilen-Padding der
    // Mono-Modes im Proportional nachbildet (BACKGROUND_PADDING_LINES × 4QL,
    // 4QL = eine Mono-Zeilenhöhe). Grenzt die Story Bible vom Haupttext ab.
    BACKGROUND_SEPARATOR: { before: 20, after: 3 },
    CODE_BLOCK: { before: 2, after: 2 },  // Console-spezifisch
    CODE_PARA:  { before: 0, after: 0 },  // Editor: Code-Block-Absatz, kein Zusatzabstand
    ENTRY_GAP: 2,         // Zwischen Console-Entries → 0.5 Zeile
    BOTTOM_PADDING: 16    // Padding am Dokumentende → 4 Zeilen (in Vierteln)
})

/**
 * Gibt die Spacing-Definition für einen Heading-Level zurück.
 * @param {number} level - Heading-Level (1-6)
 * @returns {{ before: number, after: number }}
 */
export function headingSpacing(level) {
    if (level === 1) return PARAGRAPH_SPACING.H1
    if (level === 2) return PARAGRAPH_SPACING.H2
    if (level === 3) return PARAGRAPH_SPACING.H3
    if (level === 4) return PARAGRAPH_SPACING.H4
    if (level === 5) return PARAGRAPH_SPACING.H5
    return PARAGRAPH_SPACING.H6
}

/**
 * Berechnet den effektiven Abstand zwischen zwei Absätzen (Margin-Collapsing).
 * @param {{ before: number, after: number }|null} prevSpacing - Spacing des vorherigen Absatzes (null = Dokumentanfang)
 * @param {{ before: number, after: number }} currSpacing - Spacing des aktuellen Absatzes
 * @returns {number} Effektiver Abstand in Quarter-Lines
 */
export function collapseSpacing(prevSpacing, currSpacing) {
    if (!prevSpacing) return 0  // Erste Zeile im Dokument
    return Math.max(prevSpacing.after, currSpacing.before)
}

// === ScreenCharacter Factory ===
/**
 * Erzeugt ein einzelnes ScreenCharacter-Objekt.
 * Jedes Zeichen auf dem Screen hat einen Style, der das Aussehen
 * in allen Display Modes definiert.
 *
 * @param {string} char - Das darzustellende Zeichen (1 Zeichen)
 * @param {string} style - Style-Name aus Style enum
 * @returns {Object} ScreenCharacter-Objekt
 */
export function createScreenCharacter(char = ' ', style = Style.DEFAULT) {
    return {
        char,
        style
    }
}
